import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ArtworkSubmission,
  ArtworkAttachment,
  SubmitArtworkRequest,
  MarkArtworkRequest,
  AssignmentRecord,
} from "../../types";
import {
  SubmitArtwork,
  SearchTeacherAssignments,
  EditAssignment,
} from "../../services/endpoints/artwork.endpoint";
import { logout } from "./authSlice";
import type { RootState } from "../store";

const mapRecordToSubmission = (
  record: AssignmentRecord,
  files: ArtworkAttachment[] = []
): ArtworkSubmission => {
  const studentFirstName = record["Student.First_Name"] || "";
  const studentLastName = record["Student.Last_Name"] || "";
  const studentName = `${studentFirstName} ${studentLastName}`.trim();

  const teacherName = record["Teacher.Name"] || "";

  let formattedDate = record.Created_Time || "";
  if (formattedDate.includes("T")) {
    formattedDate = formattedDate.split("T")[0];
  }

  const isGraded = record.Mark !== null && record.Mark !== undefined;

  return {
    id: record.id,
    assignment_name: record.Name,
    student_id: record.Student?.id || "",
    student_name: studentName,
    teacher_name: teacherName,
    submission_date: formattedDate,
    attachments: files,
    marks: record.Mark,
    feedback: record.Feedback,
    status: isGraded ? "Graded" : "Submitted",
    workdrive_url: record.Workdrive_Assignment_URL || undefined,
  };
};

export interface ArtworkState {
  teacherArtworks: ArtworkSubmission[];
  studentArtworks: ArtworkSubmission[];
  selectedArtwork: ArtworkSubmission | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
}

const initialState: ArtworkState = {
  teacherArtworks: [],
  studentArtworks: [],
  selectedArtwork: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
};

export const submitArtworkThunk = createAsyncThunk<
  ArtworkSubmission,
  SubmitArtworkRequest,
  { state: RootState; rejectValue: string }
>("artwork/submit", (req, { rejectWithValue }) => {
  return SubmitArtwork(req)
    .then((res) => res.data)
    .catch((err: any) => rejectWithValue(err.message));
});

export const getStudentSubmittedArtworksThunk = createAsyncThunk<
  ArtworkSubmission[],
  string | { studentId?: string; force?: boolean } | undefined,
  { state: RootState; rejectValue: string }
>(
  "artwork/getStudentArtworks",
  (param, { rejectWithValue }) => {
    const studentId = typeof param === "string" ? param : param?.studentId;
    if (!studentId) return Promise.resolve([]);
    const selectQuery = `select id, Name, Teacher, Teacher.Name, Workdrive_Assignment_URL, Mark, Feedback, Created_Time from Assignments where Student = '${studentId}' order by Created_Time desc limit 200`;

    return SearchTeacherAssignments(selectQuery)
      .then((res) => {
        const rawRecords = res.data.records;
        return rawRecords.map((record) => mapRecordToSubmission(record, []));
      })
      .catch((err: any) => rejectWithValue(err.message));
  },
  {
    condition: (param, { getState }) => {
      const { artwork } = getState();
      const force = typeof param === "object" && param?.force;
      if (force) return true;
      return artwork.studentArtworks.length === 0 && !artwork.isLoading && !artwork.error;
    },
  }
);

export const getTeacherSubmittedArtworksThunk = createAsyncThunk<
  ArtworkSubmission[],
  string | { teacherId?: string; force?: boolean } | undefined,
  { state: RootState; rejectValue: string }
>(
  "artwork/getTeacherArtworks",
  (param, { rejectWithValue }) => {
    const teacherId = typeof param === "string" ? param : param?.teacherId;
    const whereClause = teacherId ? `where Teacher = '${teacherId}'` : `where id != 0`;
    const selectQuery = `select id, Name, Student, Student.First_Name, Student.Last_Name, Workdrive_Assignment_URL, Mark, Feedback, Created_Time from Assignments ${whereClause} order by Created_Time desc limit 200`;

    return SearchTeacherAssignments(selectQuery)
      .then((res) => {
        const rawRecords = res.data.records;
        return rawRecords.map((record) => mapRecordToSubmission(record, []));
      })
      .catch((err: any) => rejectWithValue(err.message));
  },
  {
    condition: (param, { getState }) => {
      const { artwork } = getState();
      const force = typeof param === "object" && param?.force;
      if (force) return true;
      return artwork.teacherArtworks.length === 0 && !artwork.isLoading && !artwork.error;
    },
  }
);


export const markArtworkThunk = createAsyncThunk<
  { id: string; marks: number; feedback: string },
  MarkArtworkRequest,
  { state: RootState; rejectValue: string }
>("artwork/mark", (req, { rejectWithValue }) => {
  return EditAssignment({
    module: "Assignments",
    recordId: req.artwork_id,
    fields: {
      Mark: req.marks,
      Feedback: req.feedback,
    },
  })
    .then(() => {
      return {
        id: req.artwork_id,
        marks: req.marks,
        feedback: req.feedback,
      };
    })
    .catch((err: any) => rejectWithValue(err.message));
});

const artworkSlice = createSlice({
  name: "artwork",
  initialState,
  reducers: {
    setSelectedArtwork: (state, action) => {
      state.selectedArtwork = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitArtworkThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitArtworkThunk.fulfilled, (state) => {
        state.isSubmitting = false;
      })
      .addCase(submitArtworkThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || null;
      })
      .addCase(getStudentSubmittedArtworksThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getStudentSubmittedArtworksThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.studentArtworks = action.payload;
      })
      .addCase(getStudentSubmittedArtworksThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(getTeacherSubmittedArtworksThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getTeacherSubmittedArtworksThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.teacherArtworks = action.payload;
      })
      .addCase(getTeacherSubmittedArtworksThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(markArtworkThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(markArtworkThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        const updated = action.payload;
        state.teacherArtworks = state.teacherArtworks.map((item) =>
          item.id === updated.id
            ? {
                ...item,
                marks: updated.marks,
                feedback: updated.feedback,
                status: "Graded",
              }
            : item
        );
        state.selectedArtwork = null;
      })
      .addCase(markArtworkThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload || null;
      })
      .addCase(logout, (state) => {
        state.teacherArtworks = [];
        state.studentArtworks = [];
        state.selectedArtwork = null;
        state.isLoading = false;
        state.isSubmitting = false;
        state.error = null;
      });
  },
});

export const { setSelectedArtwork } = artworkSlice.actions;
export default artworkSlice.reducer;


